import { Text, VStack } from '@chakra-ui/react'
import { faCalendarDay, faCalendarWeek, faCheckCircle } from '@fortawesome/pro-solid-svg-icons'

import TextWithLines from '@/components/TextWithLines/TextWithLines'
import IconBullets, { type IconBulletItem } from '@/components/UI/IconBullets'

const SAT_BOOTCAMP_POLICY_BULLETS: IconBulletItem[] = [
  {
    key: 'miss-one-session',
    icon: faCalendarDay,
    body: (
      <Text>
        <b>You can only miss one unexcused session</b> over the four weeks. Missing more can impact
        your learning and may take a spot from another learner who wants to join.
      </Text>
    ),
  },
  {
    key: 'miss-session',
    icon: faCheckCircle,
    body: (
      <Text>
        <b>Need to miss a session?</b> Let your tutor know by using the &quot;Submit excused
        absence&quot; button on the series page. Then, catch up by using the Session Makeup Guide.
      </Text>
    ),
  },
  {
    key: 'multiple-unexcused',
    icon: faCalendarWeek,
    body: (
      <Text>
        <b>Multiple unexcused absences?</b> If you reach two unexcused absences, you may be removed
        from the series and your Schoolhouse account may be suspended.
      </Text>
    ),
  },
]

interface SeriesAttendancePolicyProps {
  attendancePolicy: string
  hasRequiredAttendanceSessions: boolean
  isSATBootcamp: boolean
}

export default function SeriesAttendancePolicy({
  attendancePolicy,
  hasRequiredAttendanceSessions,
  isSATBootcamp,
}: SeriesAttendancePolicyProps): JSX.Element {
  if (isSATBootcamp) {
    return (
      <VStack spacing="16px" align="left" width="100%">
        <Text variant="eyebrow" color="fg" align="left">
          ATTENDANCE POLICY
        </Text>

        <Text>
          To get the most out of this series and respect other learners, follow these attendance
          rules:
        </Text>

        <IconBullets
          bullets={SAT_BOOTCAMP_POLICY_BULLETS}
          iconProps={{ color: 'primary.solid', size: 'sm' }}
        />
      </VStack>
    )
  }

  return (
    <VStack
      spacing="16px"
      borderRadius="8px"
      padding="16px"
      backgroundColor="bg.muted"
      align="left"
      width="100%"
    >
      <Text variant="eyebrow" color="fg" align="left">
        ✋ ATTENDANCE POLICY
      </Text>
      <TextWithLines align="left" text={attendancePolicy} maxLines={6} />

      {hasRequiredAttendanceSessions ? (
        <Text>
          <b>Note from Schoolhouse:</b> This series has sessions with required attendance. If you
          need to miss a required session, submit an absence using the &quot;Submit Excused
          Absence&quot; button on the series page. Multiple unexcused absences may lead to a
          suspension of your Schoolhouse account.
        </Text>
      ) : null}
    </VStack>
  )
}
